import { useEffect, useState, type RefObject } from 'react';

/** Classes do `<ul>` quando a lista do chat vira carrossel no celular (#3874):
 *  cards de 320px, 16px entre eles, snap por card. `page-bleed` sangra a
 *  margem da coluna da conversa (mesma receita do `consult_places_card`). Sem
 *  barra de rolagem: os pontos embaixo são a pista de que há mais. */
export const CAROUSEL_RAIL =
  'scroll-fade-x page-bleed flex snap-x snap-mandatory gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

export const CAROUSEL_SLIDE = 'w-80 shrink-0 snap-start';

/**
 * Os pontos do carrossel: qual card está em vista, e um atalho para cada um.
 * O índice sai do `scrollLeft` do trilho (card + vão), não de um observer: é a
 * conta exata do snap, e não depende de threshold de interseção. O passo sai
 * da GEOMETRIA do rolo (largura rolável menos o respiro, dividida pelos
 * cards), e não do primeiro filho: no trilho de tarifas o filho é
 * `display: contents` (largura zero), e o card real é neto.
 */
export function CarouselDots({
  railRef,
  count,
  slideLabel,
}: {
  readonly railRef: RefObject<HTMLElement | null>;
  readonly count: number;
  /** Nome de cada ponto ("Voo 2 de 5"). O trilho de tarifas (#3875) manda o
   *  seu ("Tarifa N de M"). */
  readonly slideLabel: (position: number, total: number) => string;
}) {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const onScroll = () => {
      if (count < 1) return;
      const cs = getComputedStyle(rail);
      const gap = Number.parseFloat(cs.columnGap) || 0;
      const pad =
        (Number.parseFloat(cs.paddingLeft) || 0) + (Number.parseFloat(cs.paddingRight) || 0);
      // N cards e N-1 vãos cabem em `scrollWidth - pad`; somar um vão dá N passos.
      const step = (rail.scrollWidth - pad + gap) / count;
      setActive(step > 0 ? Math.min(count - 1, Math.round(rail.scrollLeft / step)) : 0);
    };
    rail.addEventListener('scroll', onScroll, { passive: true });
    // Sincroniza já na montagem: o trilho pode nascer rolado (remontagem,
    // restauração de scroll) e nenhum evento `scroll` viria para acertar o ponto.
    onScroll();
    return () => {
      rail.removeEventListener('scroll', onScroll);
    };
  }, [railRef, count]);
  if (count < 2) return null;
  return (
    <div className="flex h-4 items-center justify-center gap-3">
      {Array.from({ length: count }, (_, i) => (
        <button
          key={i}
          type="button"
          className={`h-1.5 rounded-full transition-[width,background-color] duration-[160ms] ease-out motion-reduce:transition-none ${
            i === active ? 'w-3 bg-foreground' : 'w-1.5 bg-ring/60'
          }`}
          aria-label={slideLabel(i + 1, count)}
          aria-current={i === active ? 'true' : undefined}
          onClick={() => {
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            railRef.current?.children[i]?.scrollIntoView({
              behavior: reduced ? 'auto' : 'smooth',
              inline: 'start',
              block: 'nearest',
            });
          }}
        />
      ))}
    </div>
  );
}
